import type { ChangeRequestPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Approval_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useEffect, useState } from 'react';

/**
 * Detects that the change request the user is looking at was resolved by
 * someone else, and calls `onResolved` once with the status it landed on.
 *
 * "By someone else" is the whole point: the selected request has to have been
 * `Pending` on the previous render and still be the same request now. A user
 * picking an already-approved request out of the dropdown is a different
 * request, not a transition, and must not fire.
 *
 * `onResolved` must be stable — it sits in the effect's dependency array.
 */
export const useChangeRequestResolution = (
  selectedChangeRequest: ChangeRequestPartsFragment | null | undefined,
  previousSelectedChangeRequest: ChangeRequestPartsFragment | null | undefined,
  onResolved: (
    changeRequest: ChangeRequestPartsFragment,
    status: Approval_Status_Enum
  ) => void
): void => {
  const [previousStatus, setPreviousStatus] =
    useState<Approval_Status_Enum | null>(null);

  useEffect(() => {
    const newStatus = selectedChangeRequest?.ChangeRequestStatus;
    if (!newStatus) {
      return;
    }

    if (
      previousStatus === Approval_Status_Enum.Pending &&
      newStatus !== Approval_Status_Enum.Pending &&
      selectedChangeRequest.Id === previousSelectedChangeRequest?.Id
    ) {
      onResolved(selectedChangeRequest, newStatus);
    }
    setPreviousStatus(newStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChangeRequest, onResolved]);
};
